import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { useApp } from "../context";
import type { SessionUser } from "../data";
import { forgetDevice } from "../mfaTrust";
import { BackupCodes, MfaSetup } from "./Mfa";

/** Settings: the user's own two-step sign in, and (owner) whether managers must use it. */
export function MfaSettings({ user, isOwner }: { user: SessionUser; isOwner: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop, mfa, reloadMfa } = useApp();
  const [mode, setMode] = useState<"view" | "setup" | "codes">("view");
  const [codes, setCodes] = useState<string[]>([]);
  const [left, setLeft] = useState<number | null>(null);
  const [error, setError] = useState<unknown>(null);
  const on = mfa?.status.factor ?? false;
  const required = mfa?.requirement.required ?? false;
  const managers = workshop.general.mfaManagers === true;

  useEffect(() => {
    if (on) void store.mfaBackupCodesLeft().then(setLeft, () => setLeft(null));
  }, [store, on, mode]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    }
  };
  const done = async () => {
    setMode("view");
    await reloadMfa?.();
  };

  return (
    <section className="mfa-settings" aria-labelledby="mfa-settings-title">
      <h2 id="mfa-settings-title">{t("mfa.settings.title")}</h2>
      {mfa?.requirement.testExempt && !on && <p className="note mfa-test-note">{t("mfa.settings.testNote")}</p>}
      {mode === "setup" ? (
        <MfaSetup store={store} userId={user.id} email={user.email} reason={mfa?.requirement.reason ?? null} inline onDone={() => void done()} onSkip={() => setMode("view")} />
      ) : mode === "codes" ? (
        <BackupCodes codes={codes} email={user.email} onDone={() => void done()} />
      ) : (
        <>
          <p className={on ? "ok" : "muted"}>{t(on ? "mfa.settings.on" : "mfa.settings.off")}</p>
          {on && left !== null && <p className={left <= 2 ? "warn-text" : "muted"}>{t("mfa.settings.codesLeft", { count: left })}</p>}
          {on && required && <p className="muted">{t("mfa.settings.required")}</p>}
          <div className="actions">
            {!on && (
              <button type="button" className="primary" onClick={() => setMode("setup")}>
                {t("mfa.settings.turnOn")}
              </button>
            )}
            {on && (
              <button type="button" onClick={() => void run(async () => { setCodes(await store.mfaNewBackupCodes()); setMode("codes"); })}>
                {t("mfa.settings.newCodes")}
              </button>
            )}
            {on && !required && (
              <button
                type="button"
                className="danger"
                onClick={() => {
                  if (!globalThis.confirm?.(t("mfa.settings.confirmOff"))) return;
                  void run(async () => {
                    await store.mfaDisable();
                    forgetDevice(user.id);
                    await reloadMfa?.();
                  });
                }}
              >
                {t("mfa.settings.turnOff")}
              </button>
            )}
          </div>
        </>
      )}
      <ErrorText error={error} />
      <label className="field switch-field">
        <span className="field-label">{t("setting.mfaManagers.label")}</span>
        <span className="switch-row">
          <input
            type="checkbox"
            checked={managers}
            disabled={!isOwner}
            aria-label={t("setting.mfaManagers.label")}
            onChange={(e) => {
              const value = e.target.checked;
              void run(async () => {
                await store.saveGeneralSettings(workshop.id, { ...workshop.general, mfaManagers: value });
                await reloadWorkshop();
              });
            }}
          />
          <span>{managers ? t("manual.default.on") : t("manual.default.off")}</span>
        </span>
        <small>{t("setting.mfaManagers.description")}</small>
      </label>
    </section>
  );
}
